import { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useDemo } from "../context/DemoContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { openDemo } = useDemo();
  const location = useLocation();
  const { t } = useTranslation();
  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { label: t("nav.ai"), to: "/ai", ai: true, tid: "joyn-ai" },
    { label: t("nav.products"), to: "/products", tid: "products" },
    { label: t("nav.who"), to: "/who-we-serve", tid: "who-we-serve" },
    { label: t("nav.support"), to: "/support", tid: "support" },
    { label: t("nav.about"), to: "/about", tid: "about" },
    { label: t("nav.contact"), to: "/contact", tid: "contact" },
  ];

  return (
    <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50 border-b border-border/70 bg-white/85 backdrop-blur-xl">
      <div className="h-1 joyn-gradient-bar" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[68px] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo height={42} />
          <span className="hidden 2xl:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground border-l border-border pl-3 whitespace-nowrap">
            <Sparkles size={11} className="text-joyn-cyan" /> {t("nav.poweredByAI")}
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.tid}`}
              className={({ isActive }) =>
                l.ai
                  ? "inline-flex items-center gap-1.5 text-[15px] font-bold whitespace-nowrap"
                  : `text-[15px] font-semibold whitespace-nowrap transition-colors ${isActive ? "text-joyn-orange" : "text-foreground/80 hover:text-foreground"}`
              }>
              {l.ai ? (
                <span className={`inline-flex items-center gap-1.5 joyn-gradient-text ${location.pathname === l.to ? "border-b-2 border-joyn-cyan pb-0.5" : ""}`}>
                  <Sparkles size={15} style={{ color: "#4BB5E7" }} className="shrink-0" /> {l.label}
                </span>
              ) : l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <LanguageToggle />
          <button data-testid="nav-schedule-demo-btn" onClick={openDemo}
            className="bg-foreground text-white text-sm font-semibold px-5 py-2.5 rounded-full whitespace-nowrap hover:-translate-y-0.5 hover:shadow-lg transition-transform">
            {t("nav.demo")}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageToggle />
          <button data-testid="mobile-menu-toggle" className="p-2 text-foreground" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-border bg-white px-4 py-6 space-y-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-link-${l.tid}`}
              className={`flex items-center gap-2 text-xl font-semibold ${l.ai ? "joyn-gradient-text" : "text-foreground"}`}>
              {l.ai && <Sparkles size={18} style={{ color: "#4BB5E7" }} />}{l.label}
            </Link>
          ))}
          <button data-testid="mobile-schedule-demo-btn" onClick={openDemo} className="w-full bg-foreground text-white font-semibold px-6 py-3.5 rounded-full mt-2 text-lg">
            {t("nav.demo")}
          </button>
        </div>
      )}
    </header>
  );
}
